import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Cog8ToothIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { getShopifyAmazonSalesCalculation } from '../../slices/inventorySlice';
import { LOW_STOCK } from '../../consts/inventoryFilter';
import { ACTIVE, enumInventoryPhasedStatus, IN_ACTIVE, PHASED_STATUSES, PLANNED, SUNSET } from '../../consts/inventoryStatus';
import { BOOLEAN_FILTERS, enumCommonBooleanStatuses, NO, YES } from '../../consts/common';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { toast } from 'react-toastify';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { getInventoryStatusColor } from '../../helpers/common';
import FilterDropdown from '../../components/FilterDropdown';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { LOW_STOCK_ALERTS } from '../../consts/reports';
import axios from 'axios';

const LowStockAlerts = ({ marginFromSidebar }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPhasedStatuses, setSelectedPhasedStatuses] = useState([ACTIVE, SUNSET]);
  const [selectedIsReplenishable, setSelectedIsReplenishable] = useState([YES, NO]);
  const [sortBy, setSortBy] = useState('warehouse_sell_out_date');
  const [sortDirection, setSortDirection] = useState('ASC');

  const {
    shopifyAmazonSales: sales,
    loading: inventoryLoading,
    error: inventoryError,
  } = useSelector((s) => s.inventory);

  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['low-stock-alerts'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['low-stock-alerts'] || [];

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    dispatch(
      getShopifyAmazonSalesCalculation({
        page: 1,
        itemsPerPage: 100000,
        search: debouncedSearch,
        activeFilter: LOW_STOCK,
        sortBy: sortBy,
        sortDirection: sortDirection,
        skipVelocity: true,
        showStockable: [],
        phasedStatuses: selectedPhasedStatuses,
        categories: [],
        isNew: [],
        isReplenishable: selectedIsReplenishable,
        demandBasisType: 'trailing',
        trailingPeriod: '365D',
        customStartDate: '',
        customEndDate: '',
        quarter: 'Q1',
        year: new Date().getFullYear().toString(),
      })
    );
  }, [dispatch, debouncedSearch, selectedPhasedStatuses, selectedIsReplenishable, sortBy, sortDirection]);

  useEffect(() => {
    dispatch(getReportEmailSettings('low-stock-alerts'));
    dispatch(getReportSendHistory('low-stock-alerts'));
  }, [dispatch]);

  const data = useMemo(() => {
    const rows = Array.isArray(sales) ? sales : [];
    return rows.map((item) => ({
      id: item?.id,
      productId: item?.productId,
      product_line: item?.productLine || '',
      sku: item?.sku || '',
      status: item?.status || '',
      on_hand: Number(item?.onHand || 0),
      onOrder: Number(item?.onOrder || 0),
      salesDailyAVG: Number(item?.salesDailyAVG || 0),
      warehouse_days_of_stock: item?.warehouseDaysOfStock ? Number(item?.warehouseDaysOfStock) : null,
      warehouse_sell_out_date: item?.warehouseSellOutDays || null,
      isReplenishable: item?.isReplenishable === true,
    }));
  }, [sales]);

  const columns = useMemo(
    () => [
      { key: 'sku', label: 'SKU', sortable: true },
      { key: 'product_line', label: 'Product Name', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'on_hand', label: 'On Hand', sortable: true },
      { key: 'onOrder', label: 'On Order', sortable: true },
      { key: 'salesDailyAVG', label: 'Daily Avg', sortable: true },
      { key: 'warehouse_days_of_stock', label: 'Days Of Stock', sortable: true },
      { key: 'warehouse_sell_out_date', label: 'Sellout Date', sortable: true },
    ],
    []
  );

  const exportColumns = useMemo(
    () => [
      { key: 'sku', label: 'SKU' },
      { key: 'product_line', label: 'Product Name' },
      { key: 'status', label: 'Status' },
      { key: 'on_hand', label: 'On Hand' },
      { key: 'onOrder', label: 'On Order' },
      { key: 'salesDailyAVG', label: 'Daily Avg' },
      { key: 'warehouse_days_of_stock', label: 'Days Of Stock' },
      { key: 'warehouse_sell_out_date', label: 'Sellout Date' },
    ],
    []
  );

  const renderRows = () => {
    return (data || []).map((row) => (
      <tr key={row.id} className="hover:bg-gray-50">
        <td className="px-4 py-2 text-sm text-gray-900">{row.sku}</td>
        <td className="px-4 py-2 text-sm text-gray-900">{row.product_line}</td>
        <td className={`px-4 py-2 text-sm text-gray-900`}>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryStatusColor(row.status)}`}>
                {row.status ? enumInventoryPhasedStatus[row.status] : ""}
            </span>
        </td>
        <td className="px-4 py-2 text-sm text-gray-900">{row.on_hand}</td>
        <td className="px-4 py-2 text-sm text-gray-900">{row.onOrder}</td>
        <td className="px-4 py-2 text-sm text-gray-900">{row.salesDailyAVG ? row.salesDailyAVG.toFixed(2) : "0"}</td>
        <td className="px-4 py-2 text-sm text-gray-900">{Math.round(Number(row.warehouse_days_of_stock || 0)) ?? "0"}</td>
        <td className="px-4 py-2 text-sm text-gray-900">{row.warehouse_sell_out_date ?? ""}</td>
      </tr>
    ));
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleSort = ({ field, direction }) => {
    setSortBy(field);
    setSortDirection(direction === 'asc' ? 'ASC' : 'DESC');
  };

  const inventoryLinkHref = useMemo(() => {
    const params = new URLSearchParams({
      page: '1',
      search: debouncedSearch || '',
      activeFilter: LOW_STOCK,
      sortBy: sortBy,
      sortDirection: sortDirection,
      showStockable: '',
      phasedStatuses: (selectedPhasedStatuses || []).join(','),
      categories: '',
      isNew: '',
      isReplenishable: (selectedIsReplenishable || []).join(','),
      filterBy: LOW_STOCK,
      demandBasisType: 'trailing',
      trailingPeriod: '365D',
      customStartDate: '',
      customEndDate: '',
      quarter: 'Q1',
      year: new Date().getFullYear().toString(),
    });

    return `${ROUTES.SHOPIFY_AMAZON_INVENTORY}?${params.toString()}`;
  }, [debouncedSearch, selectedIsReplenishable, selectedPhasedStatuses, sortBy, sortDirection]);

  const filtersComponent = (
    <div className="contents">
        <a
          className="text-sm text-blue-600 hover:underline"
          href={inventoryLinkHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          Stock Levels Low Stock Alerts Link
        </a>
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search SKU / Product"
          className="w-40 sm:w-[220px] rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      <div className="relative">
        <FilterDropdown
          options={PHASED_STATUSES.filter((status) => status !== IN_ACTIVE && status !== PLANNED)}
          selectedOptions={selectedPhasedStatuses}
          onChange={setSelectedPhasedStatuses}
          placeholder="Product Status"
          renderOption={(phase) => (
            <span className="text-gray-900">{enumInventoryPhasedStatus[phase]}</span>
          )}
          disableScrollRecalculation={true}
        />
      </div>

      <div className="relative">
        <FilterDropdown
          options={BOOLEAN_FILTERS}
          selectedOptions={selectedIsReplenishable}
          onChange={setSelectedIsReplenishable}
          placeholder="Replenishable"
          renderOption={(value) => (
            <span className="text-gray-900">{enumCommonBooleanStatuses[value]}</span>
          )}
          disableScrollRecalculation={true}
        />
      </div>

      <div className="flex items-center gap-3">
        <Cog8ToothIcon className="h-5 w-5 text-primary cursor-pointer" onClick={() => setIsOpenSettings(true)} />
      </div>
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data.map((r) => ({
        sku: r.sku,
        product_line: r.product_line,
        status: r.status ? enumInventoryPhasedStatus[r.status] || r.status : '',
        on_hand: r.on_hand ?? 0,
        onOrder: r.onOrder ?? 0,
        salesDailyAVG: r.salesDailyAVG ? r.salesDailyAVG.toFixed(2) : "0",
        warehouse_days_of_stock: r.warehouse_days_of_stock ? Math.round(Number(r.warehouse_days_of_stock)) : "0",
        warehouse_sell_out_date: r.warehouse_sell_out_date || "",
      }))}
      columns={exportColumns}
      filename="low-stock-alerts"
      onExport={async () => {
        const res = await axios.get('/api/v1/shopify-amazon-sales-calculation', {
          params: {
            page: 1,
            itemsPerPage: 100000,
            search: debouncedSearch,
            activeFilter: LOW_STOCK,
            sortBy: sortBy,
            sortDirection: sortDirection,
            showStockable: [],
            skipVelocity: true,
            phasedStatuses: selectedPhasedStatuses,
            categories: [],
            isNew: [],
            isReplenishable: selectedIsReplenishable,
            demandBasisType: 'trailing',
            trailingPeriod: '365D',
            customStartDate: '',
            customEndDate: '',
            quarter: 'Q1',
            year: new Date().getFullYear().toString(),
          },
        });

        const rows = Array.isArray(res?.data?.items) ? res.data.items : [];
        const exportRows = rows.map((r) => ({
          sku: r?.sku || '',
          product_line: r?.productLine || '',
          status: r?.status ? enumInventoryPhasedStatus[r.status] || r.status : '',
          on_hand: r?.onHand ?? 0,
          onOrder: r?.onOrder ?? 0,
          salesDailyAVG: r?.salesDailyAVG ? Number(r.salesDailyAVG).toFixed(2) : "0",
          warehouse_days_of_stock: r?.warehouseDaysOfStock ? Math.round(Number(r.warehouseDaysOfStock)) : "0",
          warehouse_sell_out_date: r?.warehouseSellOutDays || "",
        }));

        return { data: exportRows, columns: exportColumns };
      }}
    />
  );

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth ?? null,
      slackChannelId: formData?.slackChannelId ?? null,
      dayOfWeek: formData?.dayOfWeek ?? null,
      timeOfDay: formData?.timeOfDay ?? null,
      emailMessage: formData?.emailMessage ?? null,
      emailSendPreviewTo: formData?.emailSendPreviewTo ?? null,
    };

    try {
      await dispatch(updateReportEmailSettings({ reportType: 'low-stock-alerts', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'low-stock-alerts', ...payload })).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
      dispatch(getReportSendHistory('low-stock-alerts'));
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview email', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        scheduleType: formData?.type || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'low-stock-alerts', ...payload })).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
      dispatch(getReportSendHistory('low-stock-alerts'));
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  const error = inventoryError ? String(inventoryError) : null;

  return (
    <>
      <ReportTemplate
        reportId={LOW_STOCK_ALERTS}
        description="Report showing products with low stock levels"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={inventoryLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={data}
        columns={columns}
        pagination={null}
        emptyMessage="No low stock alerts found."
        enableSorting={true}
        sortField={sortBy}
        cronType={emailSettings?.cronType || null}
        sortDirection={sortDirection === 'ASC' ? 'asc' : 'desc'}
        onSort={handleSort}
      >
        {renderRows()}
      </ReportTemplate>

      <ReportEmailSettingsSidebar
        reportType={'low-stock-alerts'}
        isOpen={isOpenSettings}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        marginFromSidebar={marginFromSidebar}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="low-stock-alerts"
      />
    </>
  );
};

export default LowStockAlerts;

